import React, { useEffect, useState } from "react";

// material-ui
import {
  Box,
  Button,
  FormControl,
  Grid,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Tooltip,
  Autocomplete,
  Chip,
  useTheme,
} from "@mui/material";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import { openSnackbar } from "../../../../../../api/snackbar";

// assets
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// types
import { SnackbarProps } from "../../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../../api/userAxios";
import { Order } from "../../../../../../types/orders-managment/orders";
import { useVacancyPackagesForAutocomplete } from "../../../../../../hooks/query-hooks/useVacancyPackages";
import { useUsers } from "../../../../../../hooks/query-hooks/useUsers";
import { VacancyPackage } from "../../../../../../types/orders-managment/packages";

import { orderStatusOptions } from "./mockData";
import { usePaymentsForAutocomplete } from "../../../../../../hooks/query-hooks/usePayments";
import OrderItems from "../order-items/OrderItems";
import { useParams } from "react-router";
import MainCard from "../../../../../../components/MainCard";

// constant
const getInitialValues = (order: Order | null) => {
  if (order) {
    // const firstItemMeta = order.order_items[0].meta;
    return {
      user_id: String(order.user_id ?? ""),
      urgent: Boolean(order.urgent),
      vacancy_package_ids: order.order_items.map((item) =>
        String(item.packable.id)
      ),
      // show_logo: Boolean(firstItemMeta?.show_logo),
      // show_in_best_place: Boolean(firstItemMeta?.show_in_best_place),
      // show_in_app: Boolean(firstItemMeta?.show_in_app),
      // amount_vacancies: firstItemMeta?.vacancy_count ?? 0,

      payment_id: String(order.payment_id ?? ""),
      price: order.total_price ?? 0,
      status: order.status,
    };
  }

  return {
    user_id: "",
    urgent: false,
    vacancy_package_ids: [],
    // show_logo: false,
    // show_in_best_place: false,
    // show_in_app: false,
    // amount_vacancies: 0,
    payment_id: "",
    price: 0,
    status: "COMPLETED",
  };
};

// ==============================|| ORDER ADD/EDIT - FORM ||============================== //

const AddEditOrder = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState<Order | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const fetchOrderById = async () => {
      if (!orderId) return;
      try {
        const response = await USERAXIOS.get(`/order/${orderId}`);
        setOrder(response.data.data);
      } catch (error) {
        setOrder(null);
      }
    };
    fetchOrderById();
  }, [orderId]);

  const [companySearchTerm, setCompanySearchTerm] = useState("");
  const [packageSearchTerm, setPackageSearchTerm] = useState("");

  const { data: companies = [] } = useUsers("COMPANY");
  const {
    items: vacancyPackages,
    loading: packagesLoading,
    hasMore: hasMorePackages,
    loadMore: loadMorePackages,
  } = useVacancyPackagesForAutocomplete(20, packageSearchTerm);

  const { items: payments, hasMore, loadMore } = usePaymentsForAutocomplete(20);

  const OrderSchema = Yup.object().shape({
    user_id: Yup.string(),
    vacancy_package_ids: Yup.array(),
    price: Yup.number(),
    amount_vacancies: Yup.number(),
  });

  const formik = useFormik({
    initialValues: getInitialValues(order!),
    validationSchema: OrderSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        // const vacancyPackageIdsArray = Array.isArray(values.vacancy_package_ids)
        //   ? values.vacancy_package_ids
        //   : [values.vacancy_package_ids];

        const orderData = {
          id: orderId,
          // user_id: values.user_id,
          // vacancy_package_ids: vacancyPackageIdsArray,
          // show_logo: values.show_logo,
          // show_in_best_place: values.show_in_best_place,
          // payment_id: values.payment_id,
          // show_in_app: values.show_in_app,
          // amount_vacancies: values.amount_vacancies,
          // price: values.price,
          // urgent: values.urgent,
          status: values.status,
        };

        if (orderId) {
          const response = await USERAXIOS.post(`/order/${orderId}`, orderData);
          const updatedOrder = response.data.data;

          if (updatedOrder) {
            // setResult((prev) =>
            //   prev.map((order) =>
            //     order.id === updatedOrder.id ? updatedOrder : order
            //   )
            // );

            openSnackbar({
              open: true,
              message: "Order updated successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(`/order`, orderData);
          const newOrder = response.data.data;

          if (newOrder) {
            // setResult((prev) => [...prev, newOrder]);

            openSnackbar({
              open: true,
              message: "Order added successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
      } catch (error) {
        setSubmitting(false);

        openSnackbar({
          open: true,
          message: error.response.data.message || "Something went wrong",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  const selectedPackages = (vacancyPackages ?? []).filter((pkg) =>
    (formik.values.vacancy_package_ids ?? [])
      .map(String)
      .includes(String(pkg?.id))
  );

  const selectedCompany = companies.find(
    (company) => company.id === Number(formik.values.user_id)
  );

  // if (loading)
  //   return (
  //     <Box sx={{ p: 5 }}>
  //       <Stack direction="row" justifyContent="center">
  //         <CircularWithPath />
  //       </Stack>
  //     </Box>
  //   );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
      <FormikProvider value={formik}>
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
          <MainCard title={<FormattedMessage id="Edit order details" />}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Company Name" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Select company name for the order">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <Autocomplete
                    disabled
                    options={companies}
                    getOptionLabel={(option) => option.user_name || ""}
                    value={selectedCompany || null}
                    onChange={(event, newValue) => {
                      setFieldValue("user_id", newValue?.id || "");
                    }}
                    onInputChange={(event, newInputValue) => {
                      setCompanySearchTerm(newInputValue);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Company"
                        error={Boolean(touched.user_id && errors.user_id)}
                        helperText={touched.user_id && errors.user_id}
                      />
                    )}
                    loading={false}
                    loadingText="Loading companies..."
                    noOptionsText="No companies found"
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Packages" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Select packages for the order">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <Autocomplete
                    multiple
                    disabled
                    options={vacancyPackages ?? []}
                    getOptionLabel={(option: VacancyPackage) =>
                      theme.direction === "rtl"
                        ? option.note_trans.ar
                        : option.note
                    }
                    value={selectedPackages}
                    onChange={(event, newValue) => {
                      const selectedIds = newValue.map((pkg) => pkg.id);
                      setFieldValue("vacancy_package_ids", selectedIds);
                    }}
                    onInputChange={(event, newInputValue) => {
                      setPackageSearchTerm(newInputValue);
                    }}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          variant="outlined"
                          label={
                            theme.direction === "rtl"
                              ? option.note_trans.ar
                              : option.note
                          }
                          {...getTagProps({ index })}
                          key={option.id}
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder="Select Packages"
                        error={Boolean(
                          touched.vacancy_package_ids &&
                            errors.vacancy_package_ids
                        )}
                        helperText={
                          touched.vacancy_package_ids &&
                          errors.vacancy_package_ids
                        }
                      />
                    )}
                    loading={packagesLoading}
                    loadingText="Loading packages..."
                    noOptionsText="No packages found"
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        if (
                          listboxNode.scrollTop + listboxNode.clientHeight ===
                            listboxNode.scrollHeight &&
                          hasMorePackages
                        ) {
                          loadMorePackages();
                        }
                      },
                    }}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="order-payment_id">
                    <FormattedMessage id="Payment" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Select payment type for the order">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <Select
                    id="order-payment_id"
                    displayEmpty
                    disabled
                    {...getFieldProps("payment_id")}
                    onChange={(event: SelectChangeEvent<string>) => {
                      setFieldValue("payment_id", event.target.value);
                    }}
                    value={getFieldProps("payment_id").value || ""}
                  >
                    {payments.map((p) => (
                      <MenuItem value={p.id}>
                        {theme.direction === "rtl" ? p.name_trans.ar : p.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Stack>
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="order-amount_vacancies">
                    <FormattedMessage id="Count Vacancies" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Type count for the order">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                   <TextField
                    fullWidth
                    type="number"
                    id="order-amount_vacancies"
                    placeholder="Amount Vacancies"
                    {...getFieldProps("amount_vacancies")}
                    error={Boolean(
                      touched.amount_vacancies && errors.amount_vacancies
                    )}
                    helperText={
                      touched.amount_vacancies && errors.amount_vacancies
                    }
                  /> 
                </Stack>
              </Grid> */}

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="order-price">
                    <FormattedMessage id="Price" />
                    <span style={{ color: "red" }}>*</span>
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Type price for the order">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <TextField
                    fullWidth
                    disabled
                    type="number"
                    id="order-price"
                    placeholder="Enter Price"
                    {...getFieldProps("price")}
                    error={Boolean(touched.price && errors.price)}
                    helperText={touched.price && errors.price}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="order-status">
                    <FormattedMessage id="Status" />
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Set the order status">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <Select
                      id="order-status"
                      value={formik.values.status}
                      onChange={(event) =>
                        setFieldValue("status", event.target.value)
                      }
                    >
                      {orderStatusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}{" "}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {touched.status && errors.status && (
                    <FormHelperText error sx={{ pl: 1.75 }}>
                      {errors.status}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              {/* <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Urgent" />
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Set the Urgent value (Default no)">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.urgent}
                          {...getFieldProps("urgent")}
                          onChange={(event) =>
                            setFieldValue("urgent", event.target.checked)
                          }
                        />
                      }
                      label={
                        formik.values.urgent ? (
                          <FormattedMessage id="yes" />
                        ) : (
                          <FormattedMessage id="no" />
                        )
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Show in App" />
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Set the Show in App value (Default no)">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.show_in_app}
                          {...getFieldProps("show_in_app")}
                          onChange={(event) =>
                            setFieldValue("show_in_app", event.target.checked)
                          }
                        />
                      }
                      label={
                        formik.values.show_in_app ? (
                          <FormattedMessage id="yes" />
                        ) : (
                          <FormattedMessage id="no" />
                        )
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Show Logo" />
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Set the show logo value (Default no)">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.show_logo}
                          {...getFieldProps("show_logo")}
                          onChange={(event) =>
                            setFieldValue("show_logo", event.target.checked)
                          }
                        />
                      }
                      label={
                        formik.values.show_logo ? (
                          <FormattedMessage id="yes" />
                        ) : (
                          <FormattedMessage id="no" />
                        )
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={1}>
                  <InputLabel>
                    <FormattedMessage id="Show in Best Place" />
                    <span style={{ color: "grey", marginLeft: "5px" }}>
                      <Tooltip title="Set the Show in Best Place value (Default no)">
                        <HelpOutlineOutlinedIcon
                          style={{
                            width: "15px",
                            height: "15px",
                            cursor: "pointer",
                          }}
                        />
                      </Tooltip>
                    </span>
                  </InputLabel>
                  <FormControl fullWidth>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formik.values.show_in_best_place}
                          {...getFieldProps("show_in_best_place")}
                          onChange={(event) =>
                            setFieldValue(
                              "show_in_best_place",
                              event.target.checked
                            )
                          }
                        />
                      }
                      label={
                        formik.values.show_in_best_place ? (
                          <FormattedMessage id="yes" />
                        ) : (
                          <FormattedMessage id="no" />
                        )
                      }
                    />
                  </FormControl>
                </Stack>
              </Grid> */}
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="flex-end"
              marginTop="2rem"
              gap="1rem"
            >
              {/* <Stack direction="row" spacing={2} sx={{ width: 1 }}> */}
              <Grid item md={2}>
                <Button
                  disabled={isSubmitting}
                  fullWidth
                  type="submit"
                  variant="contained"
                >
                  <FormattedMessage id={"Update"} />
                </Button>
              </Grid>
              <Grid item md={2}>
                <Button
                  fullWidth
                  color="secondary"
                  variant="outlined"
                  // onClick={handleClose}
                >
                  <FormattedMessage id="Cancel" />
                </Button>
              </Grid>
              {/* </Stack> */}
            </Grid>
          </MainCard>
        </Form>
      </FormikProvider>
      {orderId && <OrderItems orderId={Number(orderId)} />}
    </Box>
  );
};

export default AddEditOrder;
